import Breadcrumb from '@Services/Breadcrumb';
import { databaseId as databaseIdSelector } from '@Selectors/StateParamsSelectors';
import { getAsync } from '@Containers/ResultsFilters/ResultsFiltersService';
import { relativeContextItemsByHitSetKey as relativeContextItemsByHitSetKeySelector } from '@Containers/RelativeContext/RelativeContextSelectors';
import { keyifyParamsForFiltersRequest } from '../ListOfFiltersSelectors';

import {
  filtersFetchedType,
  updateFiltersType,
  updateGroupByIdType,
  updateFilterByIdType,
  addFilterGroupsByIdType,
  resetFiltersType,
} from '../ListOfFiltersConstants';

export function expandFiltersAccordingToBreadCrumb(filters, state) {
  const databaseId = databaseIdSelector(state);
  const { filters: filtersFromBreadCrumb } = Breadcrumb.getActiveBreadcrumbForDb(databaseId);

  if (!filtersFromBreadCrumb) {
    return filters;
  }

  return filters.map((item) => {
    const { id } = item;
    const filterFromBreadCrumb = filtersFromBreadCrumb.find((filter) => filter.property === id);
    if (filterFromBreadCrumb) {
      return {
        ...item,
        expanded: true,
      };
    }

    return item;
  });
}

function isCrossContextFilterDisabled(item, currentContext, relativeContextItems) {
  // Accepts plain object or Immutable.Map
  const contextValue = typeof item?.get === 'function' ? item.get('contextValue') : item?.contextValue;
  if (contextValue && contextValue !== currentContext) {
    return !relativeContextItems || !relativeContextItems.get(contextValue);
  }
  return false;
}

export function disableCrossContextFiltersIfContextNotLoaded(filters, currentContext, relativeContextItems) {
  return filters.map((item) => ({
    ...item,
    disabled: isCrossContextFilterDisabled(item, currentContext, relativeContextItems),
  }));
}

export function enableCrossContextFiltersWhenGotRelativeContexts({ key, params, relativeContextItems, filters }) {
  return (dispatch) => {
    if (!filters) return;

    // Accepts plain array or Immutable.List
    const filtersArr = Array.isArray(filters) ? filters : filters.toJS();

    const patches = filtersArr.reduce((acc, item) => {
      const { id, disabled: prevDisabled } = item;
      const nextDisabled = isCrossContextFilterDisabled(item, params.context, relativeContextItems);

      if (prevDisabled !== nextDisabled) {
        acc[id] = { disabled: nextDisabled };
      }
      return acc;
    }, {});

    if (Object.keys(patches).length === 0) return;

    dispatch({
      type: updateFiltersType,
      payload: {
        key,
        data: patches, // { id1: { disabled: true }, ... }
      },
    });
  };
}

export function fetchFilters(params, key) {
  return async (dispatch, getState) => {
    let filters = await getAsync(params);
    const relativeContextItems = relativeContextItemsByHitSetKeySelector(getState(), { hitSetKey: params.hitSetKey });
    filters = expandFiltersAccordingToBreadCrumb(filters, getState());
    filters = disableCrossContextFiltersIfContextNotLoaded(filters, params.context, relativeContextItems);

    dispatch({
      type: filtersFetchedType,
      payload: {
        key,
        data: filters,
      },
    });

    return filters;
  };
}

export function updateFilterById(key, filterId, data) {
  return {
    type: updateFilterByIdType,
    payload: {
      key,
      data,
      filterId,
    },
  };
}

export function clearFilterState(key) {
  return {
    type: resetFiltersType,
    payload: { key },
  };
}

export function toggleFilter(filter) {
  return (dispatch, getState) => {
    const state = getState();
    const id = filter.get('id');
    const expanded = filter.get('expanded');
    const key = keyifyParamsForFiltersRequest(state);
    const action = updateFilterById(key, id, {
      expanded: !expanded,
    });
    dispatch(action);
  };
}

export function updateGroupById(key, filterId, groupId, data) {
  return {
    type: updateGroupByIdType,
    payload: {
      key,
      data,
      groupId,
      filterId,
    },
  };
}

export function addFilterGroupsById(key, filterId, groups, groupCount, thesaurusGroups, hitCount, thesaurusRootId, index) {
  return {
    type: addFilterGroupsByIdType,
    payload: {
      key,
      groups,
      filterId,
      hitCount,
      groupCount,
      thesaurusGroups,
      thesaurusRootId,
      index,
    },
  };
}
